import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Pagination,
  message,
  Spin,
  Card,
  Row,
  Col,
  Select,
  Button,
  Tag,
  Layout,
  Input,
} from "antd";
import {
  FileTextOutlined,
  FilterOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { searchSubscriptions } from "../../api/subscriptions/subscriptionsRequest";
import { getClientsWithoutPaginate } from "../../api/client/clientRequest";
import {
  getClientDisplayName,
  SUBSCRIPTION_STATUS,
} from "../../utils/constants";
import PermissionRender from "../../components/PermissionRender";
import { getSubscriptionTypes } from "../../api/subscription_types/subscriptionTypesRequest";
import { useSelector } from "react-redux";

const { Option } = Select;
const { Content } = Layout;

const SubscriptionPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [filterClientId, setFilterClientId] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterSubscriptionType, setFilterSubscriptionType] = useState(null);
  const [filterContractNumber, setFilterContractNumber] = useState("");
  const [filterPrefix, setFilterPrefix] = useState("");
  const [clients, setClients] = useState([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const user = useSelector((state) => state.user.user);
  const [isExportingExcel, setIsExportingExcel] = useState(false);

  const workerPdfRef = useRef(null);
  const workerExcelRef = useRef(null);

  useEffect(() => {
    workerPdfRef.current = new Worker(
      new URL("../../workers/pdfWorker.js", import.meta.url)
    );
    workerExcelRef.current = new Worker(
      new URL("../../workers/excelWorker.js", import.meta.url)
    );

    workerPdfRef.current.onmessage = (e) => {
      const { status, blobURL, operation, filename, message } = e.data;

      // Handle different status updates
      switch (status) {
        case "starting":
          console.log("PDF generation starting...");
          break;
        case "generating":
          console.log("Creating PDF document...");
          // You could update a more granular status message here if needed
          break;
        case "complete":
          console.log("PDF generation complete");

          if (operation === "download") {
            const link = document.createElement("a");
            link.href = blobURL;
            link.download = filename || "file.pdf";
            link.click();
            URL.revokeObjectURL(blobURL);
          } else if (operation === "open") {
            window.open(blobURL, "_blank");
          }

          break;
        case "error":
          console.error("PDF generation error:", message);
          // message.error(`Erreur lors de la génération du PDF: ${message}`);
          break;
        default:
          console.warn("Unknown status:", status);
          break;
      }
    };

    workerExcelRef.current.onmessage = (e) => {
      const { success, blobURL, filename, error } = e.data;

      if (success) {
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = filename || "file.xlsx";
        link.click();
        URL.revokeObjectURL(blobURL);
      } else {
        message.error(
          `Erreur lors de la génération du fichier Excel: ${error}`
        );
      }
    };

    return () => {
      workerPdfRef.current?.terminate();
      workerExcelRef.current?.terminate();
    };
  }, []);

  const handleExcelClick = async () => {
    setIsExportingExcel(true);
    try {
      const timestamp = new Date().toISOString().replace(/:/g, "-");
      const filename = `Abonnements_${timestamp}.xlsx`;
      const baseUrl = window.location.origin;

      const params = { no_pagination: true };
      if (filterStatus) params.status = filterStatus;
      if (filterSubscriptionType)
        params.subscription_type_id = filterSubscriptionType;
      if (filterContractNumber && filterContractNumber.trim() !== "")
        params.contract_number = filterContractNumber.trim();
      if (filterPrefix && filterPrefix.trim() !== "")
        params.prefix = filterPrefix.trim();

      const dataSource = await searchSubscriptions(params);

      // Prepare only the necessary data to reduce processing overhead
      const formattedData = dataSource.map((it) => ({
        client_name: getClientDisplayName(it.client),
        matricule: it.vehicle?.matricule || "",
        prefix: it.vehicle?.prefix || it.prefix || "",
        status: SUBSCRIPTION_STATUS[it.status] || it.status,
        contract_number: it.contract_number || "",
        subscription_type: getSubscriptionTypeDisplayName(
          it.subscription_type_id
        ),
        start_date: it.start_date,
        end_date: it.end_date,
        last_paid_month: getLastPaidMonth(it.last_paid_echeance),
        created_by: it.creator?.full_name || "",
        created_at: new Date(it.created_at).toLocaleDateString("fr-FR"),
      }));

      const data = [
        ["Abonnements", ``, ``, ``],
        [],
        [],
        user.full_name,
        [],
        [
          [
            {
              text: "Client",
              type: "string",
              key: "client_name",
            },
            {
              text: "Véhicule",
              type: "string",
              key: "matricule",
            },
            {
              text: "Macaron",
              type: "string",
              key: "prefix",
            },
            {
              text: "Statut",
              type: "string",
              key: "status",
            },
            {
              text: "Numéro de contrat",
              type: "string",
              key: "contract_number",
            },

            {
              text: "Type d'abonnement",
              type: "string",
              key: "subscription_type",
            },
            { text: "Date de début", type: "string", key: "start_date" },
            { text: "Date de fin", type: "string", key: "end_date" },
            { text: "Dernier mois payé", type: "string", key: "last_paid_month" },
            { text: "Créé par", type: "string", key: "created_by" },
            { text: "Date de création", type: "string", key: "created_at" },
          ],
        ],
        [formattedData],
        baseUrl,
      ];

      // Post message to worker
      workerExcelRef.current.postMessage({ data, filename });
    } catch (error) {
      message.error("Erreur lors de la génération du fichier Excel");
    } finally {
      setIsExportingExcel(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions(
      currentPage,
      pageSize,
      filterClientId,
      filterStatus,
      filterSubscriptionType,
      filterContractNumber,
      filterPrefix
    );
    fetchClients();
    fetchSubscriptionTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const data = await getClientsWithoutPaginate();
      setClients(data);
    } catch (error) {
      message.error("Erreur de chargement des clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchSubscriptionTypes = async () => {
    try {
      const types = await getSubscriptionTypes();
      setSubscriptionTypes(types);
    } catch (error) {
      message.error("Erreur de chargement des types d'abonnement");
    }
  };

  const fetchSubscriptions = async (
    page,
    perPage,
    clientId = null,
    status = null,
    subscriptionType = null,
    contractNumber = null,
    prefix = null
  ) => {
    setLoading(true);
    try {
      const params = {
        page: page,
        per_page: perPage,
      };

      if (clientId) params.client_id = clientId;
      if (status) params.status = status;
      if (subscriptionType) params.subscription_type_id = subscriptionType;
      if (contractNumber && contractNumber.trim() !== "") params.contract_number = contractNumber.trim();
      if (prefix && prefix.trim() !== "") params.prefix = prefix.trim();

      const data = await searchSubscriptions(params);
      setSubscriptions(data.data);
      setTotal(data.total);
    } catch (error) {
      message.error("Erreur de chargement des abonnements");
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setCurrentPage(1); // Reset to first page when filtering
    fetchSubscriptions(
      1,
      pageSize,
      filterClientId,
      filterStatus,
      filterSubscriptionType,
      filterContractNumber,
      filterPrefix
    );
  };

  const resetFilters = () => {
    setFilterClientId(null);
    setFilterStatus(null);
    setFilterSubscriptionType(null);
    setFilterContractNumber("");
    setFilterPrefix("");
    setCurrentPage(1);
    fetchSubscriptions(1, pageSize, null, null, "", null, null);
  };

  const getStatusTag = (status) => {
    const statusColors = {
      active: "green",
      pending: "gold",
      expired: "red",
      suspended: "volcano",
      // Add other status mappings as needed
    };

    const color = statusColors[status?.toLowerCase()] || "default";
    return <Tag color={color}>{SUBSCRIPTION_STATUS[status] || status}</Tag>;
  };

  const getSubscriptionTypeDisplayName = (id) => {
    const subscriptionType = subscriptionTypes.find((t) => t.id === id);
    if (!subscriptionType) return "—";
    return subscriptionType?.name?.split("Abonnement")[1] || "—";
  };

  const getLastPaidMonth = (lastPaidEcheance) => {
    if (!lastPaidEcheance || !lastPaidEcheance.start_date) {
      return "—";
    }
    
    const date = new Date(lastPaidEcheance.start_date);
    return date.toLocaleDateString("fr-FR", {
      month: "long",
      year: "numeric"
    });
  };

  const columns = [
    {
      title: "Client",
      dataIndex: ["client", "name"],
      key: "client",
      render: (text, record) => (
        <span>{getClientDisplayName(record.client) || "—"}</span>
      ),
    },
    {
      title: "Véhicule",
      dataIndex: ["vehicle", "matricule"],
      key: "vehicle",
      render: (text) => text || "—",
    },
    {
      title: "Macaron",
      key: "prefix",
      render: (_, record) => record.vehicle?.prefix || record.prefix || "—",
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Numéro de contrat",
      dataIndex: "contract_number",
      key: "contract_number",
    },
    {
      title: "Type d'abonnement",
      dataIndex: "subscription_type_id",
      key: "subscription_type_id",
      render: (id) => getSubscriptionTypeDisplayName(id),
    },
    {
      title: "Date de début",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "Date de fin",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Dernier mois payé",
      dataIndex: "last_paid_echeance",
      key: "last_paid_echeance",
      render: (lastPaidEcheance) => getLastPaidMonth(lastPaidEcheance),
    },
    {
      title: "Créé par",
      dataIndex: ["creator", "full_name"],
      key: "creator",
      render: (text) => text || "—",
    },
    {
      title: "Date de création",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => new Date(text).toLocaleDateString("fr-FR"),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => window.open(`/subscriptions/${record.id}`, "_blank")}
        >
          Voir
        </Button>
      ),
    },
  ];

  return (
    <Layout className="min-h-screen ">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="filter-card" style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col xs={24} sm={8} md={6} lg={6}>
              <Select
                placeholder="Filtrer par client"
                style={{ width: "100%" }}
                allowClear
                loading={loadingClients}
                onChange={(value) => setFilterClientId(value)}
                value={filterClientId}
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {clients.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {getClientDisplayName(client)}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6} lg={6}>
              <Select
                placeholder="Filtrer par type d'abonnement"
                style={{ width: "100%" }}
                allowClear
                onChange={(value) => setFilterSubscriptionType(value)}
                value={filterSubscriptionType}
                loading={subscriptionTypes.length === 0}
              >
                {subscriptionTypes.map((type) => (
                  <Option key={type.id} value={type.id}>
                    {type.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6} lg={6}>
              <Select
                placeholder="Filtrer par statut"
                style={{ width: "100%" }}
                allowClear
                onChange={(value) => setFilterStatus(value)}
                value={filterStatus}
              >
                {Object.entries(SUBSCRIPTION_STATUS).map(([key, value]) => (
                  <Option key={key} value={key}>
                    {value}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={8} md={6} lg={6}>
              <Button
                type="primary"
                onClick={handleFilter}
                icon={<FilterOutlined />}
                className="mr-2"
              >
                Rechercher{" "}
              </Button>
              <Button onClick={resetFilters}>Réinitialiser</Button>
            </Col>
          </Row>

          <Row gutter={16} align="middle" className="mt-4">
            <Col xs={24} sm={8} md={6} lg={6}>
              <Input
                placeholder="Filtrer par numéro de contrat"
                style={{ width: "100%" }}
                allowClear
                value={filterContractNumber}
                onChange={(e) => setFilterContractNumber(e.target.value)}
              />
            </Col>
            <Col xs={24} sm={8} md={6} lg={6}>
              <Input
                placeholder="Filtrer par N° macaron"
                style={{ width: "100%" }}
                allowClear
                value={filterPrefix}
                onChange={(e) => setFilterPrefix(e.target.value)}
                onPressEnter={handleFilter}
              />
            </Col>
          </Row>

          <div className="mt-4 flex justify-between space-x-2">
            <div></div>

            <PermissionRender requiredPermission={["subscriptions", "create"]}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => (window.location.href = "/subscriptions/create")}
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
              >
                Créer un abonnement
              </Button>
            </PermissionRender>
          </div>
        </Card>

        <Card className="table-card">
          <Spin spinning={loading}>
            <div style={{ marginBottom: 16 }} className="flex justify-end">
              <Button
                icon={<FileTextOutlined />}
                onClick={async () => await handleExcelClick()}
                className="hover:border-green-700 hover:text-green-500 mr-4"
                disabled={isExportingExcel}
              >
                Exporter Excel
              </Button>
              {/* <Dropdown
                menu={{ items: getPdfMenuItems(handlePdfClick, pdfExporting) }}
                disabled={pdfExporting}
                className="hover:border-red-700 hover:text-red-500"
              >
                <Button icon={<FileTextOutlined />} disabled={pdfExporting}>
                  Export PDF{" "}
                  {pdfExporting ? (
                    "..."
                  ) : (
                    <span style={{ marginLeft: 5 }}>▼</span>
                  )}
                </Button>
              </Dropdown> */}
            </div>
            <Table
              columns={columns}
              dataSource={subscriptions}
              pagination={false}
              rowKey="id"
              style={{ marginBottom: 16 }}
              scroll={{ x: "max-content" }}
            />
          </Spin>
          <Row justify="end" style={{ marginTop: 16 }}>
            <Pagination
              current={currentPage}
              total={total}
              pageSize={pageSize}
              onChange={(page, pageSize) => {
                setCurrentPage(page);
                setPageSize(pageSize);
              }}
              showSizeChanger
              showTotal={(total) => `Total: ${total} abonnements`}
            />
          </Row>

          <PermissionRender requiredPermission={["controle", "subscription"]}>
            <Button
              type="primary"
              onClick={() =>
                (window.location.href =
                  "/subscriptions/parking-check-simulation")
              }
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
            >
              Simulation de Vérification de Stationnement
            </Button>
          </PermissionRender>
        </Card>
      </Content>
    </Layout>
  );
};

export default SubscriptionPage;
